import { Inject, Injectable } from '@angular/core';
import { keyBy } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { withHistory } from '@sama/rxjs-core';
import { of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { TOOLS_TOKEN } from '../../cross-team-shared/annotation/shared/abstract-tool';
import { ToolName } from '../../cross-team-shared/tools-names';
import { EventTrackingValue } from '../../team-apis/core/event-tracking';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../team-apis/core/shell-connector';
import { PointCloudToolName } from '../state/point-cloud-content-state';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import { ObjectWith } from '../utils/misc-utils';
import { AbstractPointCloudTool } from './abstract-point-cloud-tool';

/**
 * This class manages the lifecycle hooks of the tools when switching tools.
 */
@Injectable()
export class ToolManager3DService extends InjectableSuperclass {
  selectedActivatedTool$ = this.store('selectedTool').$.pipe(
    map((toolName) => (toolName ? this.getTool(toolName) : undefined)),
    switchMap(
      (tool) =>
        tool?.activated$.pipe(
          filter((activated) => activated),
          take(1),
          map(() => tool),
        ) ?? of(undefined),
    ),
  );

  private toolsByName: ObjectWith<AbstractPointCloudTool>;

  constructor(
    private store: PointCloudContentStore,
    @Inject(TOOLS_TOKEN) tools: AbstractPointCloudTool[],
    @Inject(shellConnectorToken)
    private shellConnector: ShellConnector,
  ) {
    super();

    this.toolsByName = keyBy(tools, (tool): string => tool.name);
    this.setupSubscriptions();
  }

  initTool(name: ToolName) {
    const tool = this.toolsByName[name];
    if (!tool.isInitialized) {
      tool.onInit();
    }
  }

  getTool(name: PointCloudToolName) {
    const tool = this.toolsByName[name];
    this.initTool(tool.name);
    return tool;
  }

  getSelectedTool() {
    const name = this.store.state().selectedTool;
    return name && this.getTool(name);
  }

  cancelTool(eventTrackingValue: EventTrackingValue) {
    this.getSelectedTool()?.onCancel(eventTrackingValue);
  }

  private setupSubscriptions() {
    this.subscribeTo(
      this.store('selectedTool').$.pipe(withHistory(1)),
      this.activateNextTool,
    );
  }

  private async activateNextTool([toolName, previousToolName]: Array<
    PointCloudToolName | undefined
  >) {
    // Tools like superpixel can take a long time to load, we want to prevent
    // the user from interacting with the UI until the tool finishes loading
    const releaseInteractionNone =
      this.shellConnector.restrictInteractions('none');
    if (previousToolName) {
      this.getTool(previousToolName).onDeactivate();
    }
    if (toolName) {
      // `toolName` is undefined during initialization
      await this.getTool(toolName).onActivate();
    }
    releaseInteractionNone();
  }
}
